import type * as React from 'react';
import { cn } from '../lib/cn.js';

export interface PageHeaderProps extends Omit<React.ComponentProps<'header'>, 'title'> {
  title: React.ReactNode;
  description?: React.ReactNode;
  /** Buttons for the whole page, pushed to the right edge. */
  actions?: React.ReactNode;
}

/**
 * The title block every screen opens with: the page's one `h1`, a line saying
 * what it is for, and the page-level actions.
 *
 * It was a dozen hand-written copies, which is how two of them ended up on type
 * tokens the theme does not define and rendered at body size.
 */
export function PageHeader({ title, description, actions, className, ...props }: PageHeaderProps) {
  return (
    <header
      data-slot="page-header"
      className={cn('flex flex-wrap items-center gap-3', className)}
      {...props}
    >
      <div className="grid min-w-0 gap-1">
        <h1 className="font-display text-display text-ink">{title}</h1>
        {description && <p className="text-secondary text-muted">{description}</p>}
      </div>
      {actions && <div className="ml-auto flex flex-wrap items-center gap-2">{actions}</div>}
    </header>
  );
}
